<template>
  <div class="orders-page-wrapper" v-scroll="handleScroll">
    <OrderFilterBar @filter-change="handleFilterChange" />
    
    <q-infinite-scroll
      @load="onLoad"
      :offset="150"
      ref="infiniteScroll"
      :disable="isLoading || orders.length === 0"
    >
      <div class="order-list">
        <div v-if="Object.keys(order_agrupado_data).length">
          <div
            class="grupo"
            v-for="(grupo, data) in order_agrupado_data"
            v-bind:key="data"
          >
            <div class="cabecalho">
              <b>
                {{ $filters.toDateBRL(data, 'D/M/YY') }} -
                {{ $filters.capitalize($filters.toDayOfWeek(data, 'D/M/YY')) }}
              </b>
            </div>
            <order
              v-for="order in grupo"
              v-bind:key="order.UNIQUE_KEY || order.PED_CODIGO"
              :order="order"
            />
          </div>
        </div>
        <div v-if="isLoading && orders.length === 0" class="loading-container">
          {{ $t('dass.loadingOrders') }}
        </div>  
        <div v-else-if="!isLoading && orders.length === 0" class="empty-state">
          {{ $t('erro.noOrders') }}
        </div>
      </div>
      
      <template v-slot:loading>
        <div v-if="isLoadingMore" class="end-of-list">        
          <span>{{ $t('dass.loadingMoreOrders') }}</span>
        </div>
      </template>
    </q-infinite-scroll>
    
    <div v-if="!hasMore && orders.length > 0" class="end-of-list">
      <q-icon name="check_circle" size="20px" color="positive" />
      <span>{{ $t('dass.allOrdersLoaded') }}</span>
    </div>

    <transition name="q-transition--fade">
      <q-btn 
        v-show="showScrollButton"
        @click="scrollToTop"
        fab 
        icon="mdi-chevron-up" 
        :class="`shadow-4 dass-scroll-to-top-btn dass-fab-brand-${brandName}`"       
      >
        <q-tooltip class="bg-black">{{ $t('dass.backInFilters') || 'Voltar ao topo' }}</q-tooltip>
      </q-btn>
    </transition>
    
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Order from './Order'
import moment from '../../../helpers/date/moment'
import OrderFilterBar from './OrderFilterBar.vue';
import { useIsSafari } from '../../../composables/useIsSafari'
import { sanitizeParallelArrays } from '../../../helpers/functions/arrayValidator'
export default {
  name: 'OrderList',
  components: { Order, OrderFilterBar },
  setup() {
    const { brandName } = useIsSafari()
    return { brandName }
  },
  data() {
    return {
      isLoading: false,
      currentDates: {},
      showScrollButton: false 
    }
  },
  computed: {
    ...mapGetters('orders', {
      orders: 'getOrders',
      hasMore: 'getHasMore',
      isLoadingMore: 'getIsLoadingMore',
       listOccurrences: 'getOccurrences',
    }),    
    ...mapGetters('create_protocol', ['getDataConsumer']), 
    ...mapGetters('orders', ['getActiveFilterDate', 'getCacheLoaded']),  
    order_agrupado_data: function () {
      let orders = this.orders
      let grupos = {}
      
      orders.forEach((order) => {
        if (!order.hasOwnProperty('DATA')) return
        let data = order.DATA

        if (!grupos.hasOwnProperty(data)) grupos[data] = []
        grupos[data].push(order)
      })
      return grupos
    },
  },
  watch: {
    orders: {
      handler(newVal, oldVal) {
        if (!newVal.length) return
        const oldLength = oldVal ? oldVal.length : 0
        if (oldLength === 0) return
        
        const newOrders = newVal.slice(oldLength)
        
        if (newOrders.length === 0) return
        
        let listStatus = {
          ped_empresa: [], 
          ped_filial: [],
          fat_sequencia: []
        }
        const fatSequenciaSet = new Set(this.listOccurrences.map(occ => occ?.FAT_SEQUENCIA))

        newOrders.forEach((order) => {            
          if (order.STATUS_PED && !fatSequenciaSet.has(order.FAT_SEQUENCIA)) {
            listStatus.ped_empresa.push(order.PED_EMPRESA)
            listStatus.ped_filial.push(order.PED_FILIAL)
            listStatus.fat_sequencia.push(order.FAT_SEQUENCIA)
          }            
        })
        
        if (listStatus.ped_empresa.length > 0) {
          this.loadOcurrencesData(listStatus)
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('orders', ['loadOrders', 'loadMoreOrders', 'loadOccurrences']),
    ...mapActions('protocols', ['loadProtocols']),
    ...mapActions('wallet', ['sumWalletTransactions']),
    ...mapActions('create_protocol', ['loadSubjects', 'loadReasons', 'loadDataConsumer', 'loadSubReasons', 'loadBankData']),
    ...mapActions('toasts', ['showToast']),
    ...mapActions('orders', ['setFilterDate']),
    
    initDefaultDates() {
      const cachedFilter = this.getActiveFilterDate
            
      if (cachedFilter && cachedFilter.params && Object.keys(cachedFilter.params).length > 0) {
        this.currentDates = cachedFilter.params
      } else {        
        const hoje = moment().format('YYYY-MM-DD');
        const noventaDiasAtras = moment().subtract(90, 'days').format('YYYY-MM-DD');
        
        this.currentDates = {
          data_inicio: noventaDiasAtras,
          data_fim: hoje
        }
        
        this.setFilterDate({
         preset: '90',
         start: this.currentDates.data_inicio,
         end: this.currentDates.data_fim,
         params: this.currentDates
        });
      }
    },    
    handleScroll(position) {      
      this.showScrollButton = position > 300;
    },
    
    scrollToTop() {      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async loadData() {
      this.isLoading = true
      
      try {        
        const cacheLoaded = this.getCacheLoaded.ordersLoaded
        
        const loadPromises = [
          this.loadReasons({mot_ativo: 'A'}),       
          this.loadSubReasons(),
          this.loadSubjects({ass_ativo: 'S'}),
          this.loadDataConsumer(),
          this.loadProtocols(),
          this.loadBankData()
        ];
        
        if (!cacheLoaded) {
          loadPromises.push(this.loadOrders(this.currentDates));
        }

        await Promise.all(loadPromises);

        if (this.orders.length > 0) {
          this.loadOrderOccurrences()
        }
        
      } catch (error) {
        this.showToast({
          variant: 'error',
          message: error?.message || error
        })
      } finally {
        this.isLoading = false
      }
    },
    async handleFilterChange(dateParams) {
      this.currentDates = dateParams;
      this.isLoading = true;
      try {        
        await this.loadOrders(this.currentDates);
        
        if (this.$refs.infiniteScroll) {
          this.$refs.infiniteScroll.reset(); 
          this.$refs.infiniteScroll.resume(); 
        }
      } catch (error) {
        this.showToast({ variant: 'error', message: error?.message || error })
      } finally {
        this.isLoading = false;
      }
    },
    async loadOrderOccurrences() {
      const listStatus = { ped_empresa: [], ped_filial: [], fat_sequencia: [] }
      const fatSequenciaSet = new Set(this.listOccurrences.map(occ => occ?.FAT_SEQUENCIA))

      this.orders.forEach((order) => {
        if (order.STATUS_PED && !fatSequenciaSet.has(order.FAT_SEQUENCIA)) {
          listStatus.ped_empresa.push(order.PED_EMPRESA)
          listStatus.ped_filial.push(order.PED_FILIAL)
          listStatus.fat_sequencia.push(order.FAT_SEQUENCIA)
        }
      })
      
      if (listStatus.ped_empresa.length > 0) {
        this.loadOcurrencesData(listStatus)
      }
    },
    
    async loadOcurrencesData(listStatus) {    
      if (!listStatus) return;
      const keysToValidate = ['ped_empresa', 'ped_filial', 'fat_sequencia'];      
      const cleanedStatus = sanitizeParallelArrays(listStatus, keysToValidate);
      
      if (!cleanedStatus.fat_sequencia?.length) {        
        return;
      }
      
      await this.loadOccurrences({
        ped_empresa: cleanedStatus.ped_empresa,
        ped_filial: cleanedStatus.ped_filial,
        fat_sequencia: cleanedStatus.fat_sequencia
      }).catch((err) => {
        this.showToast({
          variant: 'error',
          message: err?.message || err
        });
      });
    },
    async onLoad(index, done) {      
      if (!this.hasMore) {
        done(true) 
        return
      }
      
      try {
        await this.loadMoreOrders(this.currentDates)
        done(!this.hasMore)
      } catch (error) {
        this.showToast({
          variant: 'error',
          message: error?.message || error
        })
        done(true)  
      }
    }
  },
  async created() {
    this.initDefaultDates();
  },
  async mounted() {
    await this.loadData()
    
    if (this.getDataConsumer?.length && this.getDataConsumer[0]?.CON_ID) {
      this.sumWalletTransactions({con_id: this.getDataConsumer[0]?.CON_ID})
    }
  }
}
</script>

<style lang="scss" scoped>
.orders-page-wrapper {
  position: relative;
  width: 100%;
}
.order-list {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 0 var(--space);
  > div {
    width: 100%;
  }
  @media (max-width: 576px) {
    padding: 0;
  }
  .grupo {
    width: 100%;
    margin-bottom: var(--space__1-5x);

    .cabecalho {
      margin: var(--space) 0;
      @media (max-width: 576px) {
        margin: var(--space__0-25x) 0;
      }
    }
  }
}
.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space__2x);
  min-height: 200px;
  color: var(--theme-gray);
}
.end-of-list {
  display: flex;  
  align-items: center;
  justify-content: center;
  padding: var(--space);
  gap: var(--space__0-5x);
  opacity: 0.7;
  
  span {
    color: var(--theme-gray);
    font-size: 13px;
  }
}

.dass-scroll-to-top-btn {
  position: fixed;
  left: 10px;
  bottom: 80px; 
  z-index: 2000;
}

@media (min-width: 700px) {
  .dass-scroll-to-top-btn {    
    right: 10px;
    left: auto;
  }
}
.dass-fab-brand-umbro {
  color: var(--theme-white) !important;
  background-color: var(--color-default-umbro) !important;
  border-radius: var(--border-radius-button-umbro, 0px) !important;
  transition: opacity 0.3s ease;
  &:hover { opacity: 0.9; }
}

.dass-fab-brand-fila {
  color: var(--theme-white) !important;
  background-color: var(--color-fila-primary) !important;
  border-radius: var(--border-radius-button-fila, 50%) !important;
  transition: opacity 0.3s ease;
  &:hover { opacity: 0.9; }
}

.dass-fab-brand-newbalance {
  color: var(--theme-white) !important;
  background-color: var(--color-default-new-balance) !important;
  border-radius: var(--border-radius-button-newbalance, 4px) !important;
  transition: opacity 0.3s ease;
  &:hover { opacity: 0.9; }
}
</style>